// The one notice host (utils/notices.js is the store) and LiveNotice, a
// notice that lives exactly as long as the component that owns it.
import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { subscribeNotices, dismissNotice, showNotice } from '../../utils/notices';
import './Notice.css';

const ICONS = {
  success: <path d="M5 12.5l4.5 4.5L19 7.5" />,
  info: <><circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 7.5v.01" /></>,
  warning: <><path d="M12 3.5l9.5 16.5h-19z" /><path d="M12 10v4.5M12 17v.01" /></>,
  error: <><circle cx="12" cy="12" r="9" /><path d="M9 9l6 6M15 9l-6 6" /></>,
};

function Glyph({ kind }) {
  if (kind === 'progress') return <span className="pnt-spin" aria-hidden="true" />;
  return (
    <svg className="pnt-ico" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {ICONS[kind] || ICONS.info}
    </svg>
  );
}

export function NoticeHost() {
  const [list, setList] = useState([]);
  useEffect(() => subscribeNotices(setList), []);
  if (typeof document === 'undefined') return null;
  return createPortal(
    <div className="pnt-stack" aria-live="polite">
      {list.map(n => (
        <div key={n.id} className={`pnt pnt--${n.kind}`} role={n.kind === 'error' ? 'alert' : 'status'}>
          <Glyph kind={n.kind} />
          <div className="pnt-text">
            {n.title && <div className="pnt-title">{n.title}</div>}
            {n.detail && <div className="pnt-detail">{n.detail}</div>}
          </div>
          {n.actions.map((a, i) => (
            <button key={i} type="button" className="pnt-act" onClick={() => a.onClick?.()}>{a.label}</button>
          ))}
          <button type="button" className="pnt-x" aria-label="Dismiss" onClick={() => { n.onDismiss?.(); dismissNotice(n.id); }}>×</button>
        </div>
      ))}
    </div>,
    document.body,
  );
}

/**
 * A notice owned by a component: shown while mounted (and while `show`),
 * updated in place when its content changes, removed on unmount. The ×
 * calls `onDismiss`, so the owner can clear its own state.
 */
export function LiveNotice({ show = true, kind, title, detail, actions, onDismiss }) {
  const liveId = `live-${useId()}`;
  const idRef = useRef(liveId);
  const dismissRef = useRef(onDismiss);
  useEffect(() => { dismissRef.current = onDismiss; });
  const actionKey = (actions || []).map(a => a.label).join('|');
  const actionsRef = useRef(actions);
  useEffect(() => { actionsRef.current = actions; });
  useEffect(() => {
    const id = idRef.current;
    if (!show || (!title && !detail)) { dismissNotice(id); return undefined; }
    showNotice({
      id, kind, title, detail,
      // Actions read through the ref, so a caller's fresh closures are used.
      actions: (actionsRef.current || []).map((a, i) => ({ label: a.label, onClick: () => actionsRef.current?.[i]?.onClick?.() })),
      // A live notice's lifetime is its owner's; the owner decides when it goes.
      duration: 0,
      onDismiss: () => dismissRef.current?.(),
    });
    return undefined;
  }, [show, kind, title, detail, actionKey]);
  useEffect(() => () => dismissNotice(idRef.current), []);
  return null;
}
